import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import { Chart, Leaf, Wave, Alert, ArrowRight } from '@/components/HypIcons';
import AnchorOverlay from '@/pages/AnchorOverlay';
import SomaticCheckin from '@/components/SomaticCheckin';
import WelcomeSheet from '@/components/WelcomeSheet';
import IkiSymbol from '@/components/IkiSymbol';
import ERITrendChart from '@/components/ERITrendChart';
import { useSubscription } from '@/hooks/useSubscription';
import CrisisScreen from '@/components/CrisisScreen';
import axios from 'axios';
import { toast } from 'sonner';
import orbUrl from '../orbAsset';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// HYPSEYA Brand Assets
const wordmarkUrl = "/hypseya-wordmark.png";

// Arrival Screen - Pulsating Logo (wordmark)
function ArrivalScreen({ onContinue }) {
  const [fadeOut, setFadeOut] = useState(false);
  const [canInteract, setCanInteract] = useState(false);

  // Micro pause before interaction is possible (2.5 seconds)
  useEffect(() => {
    const timer = setTimeout(() => {
      setCanInteract(true);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const handleContinue = () => {
    if (!canInteract) return;
    setFadeOut(true);
    setTimeout(() => onContinue(), 600);
  };

  return (
    <div 
      onClick={handleContinue}
      style={{
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'linear-gradient(160deg, #fafbfc 0%, #f0f7f6 35%, #e8f3f1 60%, #f5f8f7 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: canInteract ? 'pointer' : 'default',
        zIndex: 2000,
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 0.6s ease-out'
      }}
    >
      {/* Orb with heartbeat + ripple rings */}
      <div style={{ opacity: 0, animation: 'hyp-fade-in 1.2s ease-out 0.3s forwards' }}>
        <div className="hyp-orb-wrapper" style={{ width: 'min(210px, 54vw)', height: 'min(210px, 54vw)' }}>
          <span className="hyp-ripple-1" />
          <span className="hyp-ripple-2" />
          <img
            src={orbUrl}
            alt="HYPSEYA"
            style={{ width: '100%', height: 'auto' }}
          />
        </div>
      </div>

      {/* Wordmark */}
      <div style={{ marginTop: '20px', opacity: 0, animation: 'hyp-fade-in 1s ease-out 1.8s forwards' }}>
        <img
          src={wordmarkUrl}
          alt="HYPSEYA"
          style={{ width: 'min(190px, 48vw)', height: 'auto' }}
        />
      </div>

      {/* Tagline */}
      <div style={{
        marginTop: '32px',
        textAlign: 'center',
        opacity: 0,
        animation: 'fade-in 1s ease-out 2.6s forwards',
      }}>
        <p style={{
          fontFamily: "'Playfair Display', serif",
          color: '#1E6B68',
          fontSize: 'clamp(1rem, 2.8vw, 1.1rem)',
          margin: '0 0 10px',
          fontWeight: '400',
          letterSpacing: '0.01em',
          fontStyle: 'italic'
        }}>
          For people doing the deeper work.
        </p>
        <p style={{
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          color: '#7A9E9B',
          fontSize: 'clamp(0.78rem, 2vw, 0.85rem)',
          margin: 0,
          maxWidth: '260px',
          lineHeight: '1.7',
          fontWeight: '300',
          letterSpacing: '0.02em'
        }}>
          Whatever opened you —<br />HYPSEYA helps you bring it back.
        </p>
      </div>

      {/* Tap prompt */}
      <p style={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: '#a8b5b3',
        fontSize: '0.75rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        marginTop: '52px',
        opacity: canInteract ? 0.6 : 0,
        transition: 'opacity 1.5s ease-out',
        fontWeight: '400'
      }}>
        Tap to begin
      </p>

      {/* CSS Animations (global brand CSS handles orb pulse) */}
      <style>{`
        @keyframes fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Focus selection modal
function FocusHorizonModal({ onComplete, onClose }) {
  const [step, setStep] = useState(1);
  const [selectedFocus, setSelectedFocus] = useState(null);
  const [selectedHorizon, setSelectedHorizon] = useState(null);
  const [focusAreas, setFocusAreas] = useState([]);

  useEffect(() => {
    const loadFocusAreas = async () => {
      try {
        const response = await axios.get(`${API}/training/focus-areas`);
        setFocusAreas(response.data);
      } catch (error) {
        console.error('Error loading focus areas:', error);
      }
    };
    loadFocusAreas();
  }, []);

  const handleComplete = () => {
    if (selectedFocus) {
      onComplete(selectedFocus, selectedHorizon);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(30, 60, 60, 0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #F7F3EC 0%, #EFE7DD 100%)',
        borderRadius: '24px', maxWidth: '500px', width: '100%',
        padding: '32px', maxHeight: '85vh', overflow: 'auto',
        position: 'relative',
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16,
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: '1.2rem', color: '#999', lineHeight: 1,
          padding: '4px 8px',
        }}>✕</button>

        {step === 1 ? (
          <>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              color: '#1E6B68', fontSize: '1.5rem', marginBottom: '8px'
            }}>What are you working on?</h2>
            <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '24px' }}>
              Choose a focus area for your practice. You can change this anytime.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {focusAreas.map(area => (
                <button
                  key={area.id}
                  onClick={() => setSelectedFocus(area.id)}
                  data-testid={`focus-option-${area.id}`}
                  style={{
                    padding: '16px 20px',
                    background: selectedFocus === area.id 
                      ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: selectedFocus === area.id ? '#fff' : '#1E6B68',
                    border: selectedFocus === area.id ? 'none' : '2px solid #B8D3CF',
                    borderRadius: '16px', cursor: 'pointer',
                    textAlign: 'left', transition: 'all 0.2s ease',
                    display: 'flex', alignItems: 'flex-start', gap: '14px'
                  }}
                >
                  <div style={{ 
                    flexShrink: 0,
                    opacity: selectedFocus === area.id ? 1 : 0.8,
                    filter: selectedFocus === area.id ? 'brightness(10)' : 'none'
                  }}>
                    <IkiSymbol name={area.icon} size="md" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <strong>{area.name}</strong>
                    <p style={{ 
                      margin: '4px 0 0', fontSize: '0.85rem', 
                      opacity: selectedFocus === area.id ? 0.9 : 0.7 
                    }}>{area.description}</p>
                  </div>
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setStep(2)}
              disabled={!selectedFocus}
              style={{
                marginTop: '24px', padding: '16px 32px', width: '100%',
                background: selectedFocus 
                  ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)' 
                  : '#ccc',
                color: '#fff', border: 'none', borderRadius: '24px',
                fontSize: '1.1rem', fontWeight: '600', cursor: selectedFocus ? 'pointer' : 'not-allowed'
              }}
            >Continue</button>
          </>
        ) : (
          <>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              color: '#1E6B68', fontSize: '1.5rem', marginBottom: '8px'
            }}>Choose your practice horizon</h2>
            <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '24px' }}>
              This is not a promise. It is a container for your intention.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[
                { days: 30, label: '30 days', desc: 'Build a foundation' },
                { days: 60, label: '60 days', desc: 'Deepen the practice' },
                { days: 90, label: '90 days', desc: 'Sustainable change' },
                { days: null, label: 'Open-ended', desc: 'No timeline, just presence' }
              ].map(option => (
                <button
                  key={option.label}
                  onClick={() => setSelectedHorizon(option.days)}
                  style={{
                    padding: '16px 20px',
                    background: selectedHorizon === option.days 
                      ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: selectedHorizon === option.days ? '#fff' : '#1E6B68',
                    border: selectedHorizon === option.days ? 'none' : '2px solid #B8D3CF',
                    borderRadius: '16px', cursor: 'pointer',
                    textAlign: 'left', transition: 'all 0.2s ease'
                  }}
                >
                  <strong>{option.label}</strong>
                  <p style={{ 
                    margin: '4px 0 0', fontSize: '0.85rem', 
                    opacity: selectedHorizon === option.days ? 0.9 : 0.7 
                  }}>{option.desc}</p>
                </button>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  padding: '14px 24px', background: 'transparent',
                  color: '#1E6B68', border: '2px solid #1E6B68',
                  borderRadius: '20px', cursor: 'pointer', flex: 1
                }}
              >Back</button>
              <button
                onClick={handleComplete}
                style={{
                  padding: '14px 24px', flex: 2,
                  background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                  color: '#fff', border: 'none', borderRadius: '20px',
                  fontSize: '1rem', fontWeight: '600', cursor: 'pointer'
                }}
              >Begin Practice</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Daily Loop Step Component
function LoopStep({ number, label, isActive, isComplete, onClick }) {
  return (
    <div 
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        cursor: onClick ? 'pointer' : 'default', transition: 'all 0.3s ease',
        position: 'relative', zIndex: 1
      }}
    >
      <div style={{
        width: '48px', height: '48px', borderRadius: '50%',
        background: isComplete
          ? 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)'
          : isActive
            ? 'linear-gradient(135deg, #2D8B87 0%, #4AACA8 100%)'
            : 'rgba(184, 211, 207, 0.3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: (isComplete || isActive) ? '#fff' : '#1E6B68',
        fontSize: '1.2rem', fontWeight: '600',
        boxShadow: isActive ? '0 4px 15px rgba(30, 107, 104, 0.3)' : 'none',
        transition: 'all 0.3s ease'
      }}>
        {isComplete ? '✓' : number}
      </div>
      <span style={{
        marginTop: '8px', fontSize: '0.8rem', color: isActive ? '#1E6B68' : '#666',
        fontWeight: isActive ? '600' : '400'
      }}>{label}</span>
    </div>
  );
}

// ── Scan Slider — reusable 3-signal component ─────────────────────────────
function ScanSlider({ label, sublabel, min, max, value, onChange, testId, leftLabel, rightLabel, compact }) {
  return (
    <div style={{ marginBottom: compact ? '12px' : '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
        <label style={{ fontSize: '0.88rem', color: '#1E6B68', fontWeight: '600', letterSpacing: '0.02em' }}>
          {label}
        </label>
        <span style={{
          background: '#1E6B68', color: '#fff',
          padding: '2px 10px', borderRadius: '10px',
          fontSize: '0.85rem', fontWeight: '700', minWidth: '28px', textAlign: 'center'
        }}>{value}</span>
      </div>
      {sublabel && !compact && (
        <p style={{ margin: '0 0 8px', fontSize: '0.78rem', color: '#8FA3A0', fontStyle: 'italic' }}>{sublabel}</p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '0.72rem', color: '#AAB8B6', whiteSpace: 'nowrap' }}>{leftLabel}</span>
        <input
          type="range" min={min} max={max} value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          data-testid={testId}
          style={{ flex: 1, accentColor: '#1E6B68', height: '6px' }}
        />
        <span style={{ fontSize: '0.72rem', color: '#AAB8B6', whiteSpace: 'nowrap' }}>{rightLabel}</span>
      </div>
    </div>
  );
}

// Orient Step — mandatory 3-signal body scan
function OrientStep({ content, programContext, onComplete }) {
  const [activation, setActivation] = useState(5);
  const [presence, setPresence]     = useState(3);
  const [openness, setOpenness]     = useState(3);
  const isMobile = window.innerWidth < 500;
  const navigate = useNavigate();

  const handleComplete = () => {
    onComplete({ pre_activation: activation, pre_presence: presence, pre_openness: openness });
  };

  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', borderRadius: '24px',
      padding: isMobile ? '20px' : '32px',
      boxShadow: '0 4px 20px rgba(30,107,104,0.08)'
    }}>
      <div style={{
        fontSize: '0.75rem', color: '#2D8B87', textTransform: 'uppercase',
        letterSpacing: '0.12em', marginBottom: '12px', fontWeight: '600'
      }}>Orient</div>

      {/* Program week context — the enrolled program frames today's session */}
      {programContext?.theme && (
        <button
          onClick={() => navigate('/programs')}
          style={{
            display: 'block', width: '100%', textAlign: 'left',
            background: `linear-gradient(135deg, ${programContext.color || '#1E6B68'}0F, ${programContext.color || '#1E6B68'}05)`,
            border: `1px solid ${programContext.color || '#1E6B68'}2E`,
            borderRadius: '14px', padding: '12px 16px',
            marginBottom: '18px', cursor: 'pointer',
          }}
        >
          <span style={{
            fontSize: '0.62rem', fontWeight: '700', letterSpacing: '0.08em',
            textTransform: 'uppercase', color: programContext.color || '#1E6B68',
          }}>
            {programContext.program_title} · Week {programContext.week} of {programContext.total_weeks}
          </span>
          <span style={{
            display: 'block', marginTop: '3px',
            fontFamily: "'Playfair Display', serif", fontSize: '0.98rem', color: '#1E3A3A',
          }}>{programContext.theme}</span>
          <span style={{ display: 'block', marginTop: '2px', fontSize: '0.75rem', color: '#7A9E9B', lineHeight: 1.5 }}>
            {programContext.focus}
          </span>
        </button>
      )}

      <p style={{
        fontFamily: "'Georgia', serif", fontSize: isMobile ? '1.05rem' : '1.2rem',
        lineHeight: '1.7', color: '#333', margin: isMobile ? '0 0 16px' : '0 0 28px'
      }}>{content}</p>

      {/* 30-second body scan */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(30,107,104,0.04) 0%, rgba(184,211,207,0.12) 100%)',
        borderRadius: '16px', padding: isMobile ? '16px' : '24px',
        marginBottom: isMobile ? '16px' : '24px',
        borderLeft: '3px solid #B8D3CF'
      }}>
        <p style={{
          margin: isMobile ? '0 0 12px' : '0 0 20px', fontSize: '0.75rem', color: '#8FA3A0',
          letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600'
        }}>30-second body scan — before practice</p>

        <ScanSlider
          label="Activation" sublabel="How much activation or tension do you notice right now?"
          min={1} max={10} value={activation} onChange={setActivation}
          testId="pre-activation-slider" leftLabel="1 — calm" rightLabel="10 — activated"
          compact={isMobile}
        />
        <ScanSlider
          label="Presence" sublabel="How connected are you to your body in this moment?"
          min={1} max={5} value={presence} onChange={setPresence}
          testId="pre-presence-slider" leftLabel="Disconnected" rightLabel="Fully present"
          compact={isMobile}
        />
        <ScanSlider
          label="Openness" sublabel="How receptive does your system feel right now?"
          min={1} max={5} value={openness} onChange={setOpenness}
          testId="pre-openness-slider" leftLabel="Contracted" rightLabel="Open"
          compact={isMobile}
        />
      </div>

      <button
        onClick={handleComplete}
        data-testid="orient-complete-btn"
        style={{
          padding: isMobile ? '14px 40px' : '16px 40px', width: '100%',
          background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
          color: '#fff', border: 'none', borderRadius: '24px',
          fontSize: '1rem', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.02em'
        }}
      >Begin Practice</button>
    </div>
  );
}

// Practice Step View — the adaptive n-of-1 engine picks today's practice
function PracticeStep({ recommendation, onComplete }) {
  const [activePractice, setActivePractice] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const rec = recommendation?.recommended;
  const library = recommendation?.library || [];
  const statsById = {};
  (recommendation?.effectiveness || []).forEach(s => { statsById[s.practice_id] = s; });
  const alternatives = library.filter(p => p.id !== rec?.id);
  const isPersonalised = recommendation?.engine_state === 'personalised';

  const practiceMinutes = (p) =>
    Math.max(2, Math.round(p.phases.reduce((s, ph) => s + ph.duration, 0) / 60));

  const handleOverlayClose = () => {
    const p = activePractice;
    setActivePractice(null);
    if (p) onComplete(p.id, practiceMinutes(p));
  };

  const deltaChip = (pid) => {
    const s = statsById[pid];
    if (!s || s.avg_regulation_delta == null) return null;
    const d = s.avg_regulation_delta;
    return (
      <span style={{
        fontSize: '0.7rem', fontWeight: '700', whiteSpace: 'nowrap',
        color: d >= 0 ? '#1E6B68' : '#B8906A',
        background: d >= 0 ? 'rgba(30,107,104,0.08)' : 'rgba(184,144,106,0.12)',
        borderRadius: '10px', padding: '3px 8px',
      }}>{d >= 0 ? '−' : '+'}{Math.abs(d).toFixed(1)} activation · {s.measured_sessions}×</span>
    );
  };

  if (!recommendation) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)', borderRadius: '24px',
        padding: '32px', boxShadow: '0 4px 20px rgba(30, 107, 104, 0.08)',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '0.75rem', color: '#2D8B87', textTransform: 'uppercase',
          letterSpacing: '0.12em', marginBottom: '16px', fontWeight: '600'
        }}>Practice</div>
        <p style={{ color: '#8FA3A0', fontSize: '0.9rem' }}>Reading your data…</p>
      </div>
    );
  }

  return (
    <>
      <div style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '24px', padding: '32px',
        boxShadow: '0 4px 20px rgba(30, 107, 104, 0.08)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '16px'
        }}>
          <div style={{
            fontSize: '0.75rem', color: '#2D8B87',
            textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: '600'
          }}>Practice</div>
          <div style={{
            fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.06em',
            color: isPersonalised ? '#1E6B68' : '#8FA3A0',
            background: isPersonalised ? 'rgba(30,107,104,0.08)' : 'rgba(143,163,160,0.12)',
            borderRadius: '12px', padding: '4px 10px', textTransform: 'uppercase'
          }}>{isPersonalised ? 'Personalised' : 'Calibrating'}</div>
        </div>

        {rec ? (
          <>
            <p style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.15rem', color: '#1E6B68', margin: '0 0 6px'
            }}>{rec.name}</p>
            <p style={{
              fontSize: '0.88rem', color: '#8FA3A0', marginBottom: '16px', lineHeight: '1.6'
            }}>{rec.tagline}</p>

            {recommendation.reason && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(30,107,104,0.04) 0%, rgba(184,211,207,0.12) 100%)',
                borderLeft: '3px solid #B8D3CF', borderRadius: '12px',
                padding: '14px 16px', marginBottom: '20px'
              }}>
                <p style={{
                  margin: 0, fontSize: '0.82rem', color: '#4A6B68', lineHeight: '1.6'
                }}>{recommendation.reason}</p>
              </div>
            )}

            <button
              onClick={() => setActivePractice(rec)}
              data-testid="start-practice-btn"
              style={{
                padding: '18px', width: '100%',
                background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                border: 'none', borderRadius: '16px',
                cursor: 'pointer', color: '#fff'
              }}
            >
              <strong style={{ fontSize: '1.05rem' }}>Begin {rec.name}</strong>
              <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
                ~{practiceMinutes(rec)} minutes · guided
              </p>
            </button>
          </>
        ) : (
          <button
            onClick={() => setActivePractice({ id: 'regulation_anchor', name: 'Regulation Anchor', phases: [] })}
            style={{
              padding: '18px', width: '100%',
              background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              border: 'none', borderRadius: '16px', cursor: 'pointer', color: '#fff'
            }}
          >
            <strong style={{ fontSize: '1.05rem' }}>Begin Regulation Anchor</strong>
            <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: '0.85rem' }}>~2 minutes · guided</p>
          </button>
        )}

        {alternatives.length > 0 && (
          <>
            <button
              onClick={() => setShowAll(v => !v)}
              style={{
                marginTop: '16px', padding: '10px', width: '100%',
                background: 'transparent', color: '#2D8B87',
                border: 'none', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '600'
              }}
            >{showAll ? 'Hide other practices' : 'Choose a different practice'}</button>

            {showAll && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {alternatives.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setActivePractice(p)}
                    style={{
                      padding: '14px 16px',
                      background: 'linear-gradient(135deg, rgba(30,107,104,0.04) 0%, rgba(184,211,207,0.1) 100%)',
                      border: '1.5px solid rgba(184,211,207,0.6)', borderRadius: '14px',
                      textAlign: 'left', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px'
                    }}
                  >
                    <span>
                      <strong style={{ color: '#1E6B68', fontSize: '0.92rem', display: 'block' }}>{p.name}</strong>
                      <span style={{ color: '#8FA3A0', fontSize: '0.78rem', lineHeight: '1.4' }}>{p.tagline}</span>
                    </span>
                    {deltaChip(p.id)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <button
          onClick={() => onComplete(null, 5)}
          style={{
            marginTop: '16px', padding: '12px', width: '100%',
            background: 'transparent', color: '#666',
            border: '1px solid #ccc', borderRadius: '16px',
            fontSize: '0.9rem', cursor: 'pointer'
          }}
        >I completed my practice elsewhere</button>
      </div>

      {activePractice && <AnchorOverlay practice={activePractice} onClose={handleOverlayClose} />}
    </>
  );
}

// ── SessionDelta — before/after comparison card ────────────────────────────
function SessionDelta({ pre, post }) {
  if (!pre?.activation) return null;

  const signals = [
    {
      key: 'activation', label: 'Activation',
      pre: pre.activation,  post: post.activation,
      max: 10,
      // lower = better for activation
      improved: post.activation < pre.activation,
      delta: pre.activation - post.activation
    },
    {
      key: 'presence', label: 'Presence',
      pre: pre.presence,    post: post.presence,
      max: 5,
      // higher = better for presence
      improved: post.presence > pre.presence,
      delta: post.presence - pre.presence
    },
    {
      key: 'openness', label: 'Openness',
      pre: pre.openness,    post: post.openness,
      max: 5,
      // higher = better for openness
      improved: post.openness > pre.openness,
      delta: post.openness - pre.openness
    }
  ];

  return (
    <div style={{
      background: 'rgba(255,255,255,0.7)',
      border: '1px solid rgba(184,211,207,0.5)',
      borderRadius: '14px', padding: '18px 20px', marginBottom: '20px'
    }}>
      <p style={{
        margin: '0 0 14px', fontSize: '0.72rem', color: '#8FA3A0',
        letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600'
      }}>Session shift</p>
      <div style={{ display: 'flex', gap: '12px' }}>
        {signals.map(s => {
          const color = s.delta === 0 ? '#AAB8B6'
            : s.improved ? '#2D8B87' : '#B8906A';
          const sign  = s.delta > 0 ? '+' : '';
          return (
            <div key={s.key} style={{
              flex: 1, textAlign: 'center',
              background: s.delta === 0
                ? 'rgba(200,200,200,0.08)'
                : s.improved
                  ? 'rgba(45,139,135,0.07)'
                  : 'rgba(184,144,106,0.07)',
              borderRadius: '10px', padding: '12px 8px'
            }}>
              <div style={{ fontSize: '0.7rem', color: '#8FA3A0', marginBottom: '4px', fontWeight: '600', letterSpacing: '0.06em' }}>
                {s.label.toUpperCase()}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>
                {s.pre} <span style={{ color: '#B8D3CF', margin: '0 2px' }}>→</span> {s.post}
              </div>
              <div style={{ fontSize: '1rem', fontWeight: '700', color }}>
                {s.delta === 0 ? '—' : `${sign}${s.delta}`}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Observe Step View - Enhanced with numeric activation scales for ERI
function ObserveStep({ onComplete, onSkip, preActivation, prePresence, preOpenness }) {
  const [postActivation, setPostActivation] = useState(preActivation || 5);
  const [postPresence,   setPostPresence]   = useState(3);
  const [postOpenness,   setPostOpenness]   = useState(3);
  const [note, setNote] = useState('');

  const handleSubmit = () => {
    onComplete({
      post_activation: postActivation,
      post_presence:   postPresence,
      post_openness:   postOpenness,
      body_tension:    6 - postPresence, // backwards compat
      clarity:         postOpenness,
      note: note || null,
      emotional_tone: postActivation <= 3 ? 'calmer' : postActivation >= 7 ? 'activated' : 'same',
      clarity_legacy: postOpenness >= 4 ? 'yes' : postOpenness === 3 ? 'unsure' : 'no'
    });
  };

  const preData = {
    activation: preActivation,
    presence:   prePresence,
    openness:   preOpenness
  };

  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', borderRadius: '24px',
      padding: '32px', boxShadow: '0 4px 20px rgba(30,107,104,0.08)'
    }}>
      <div style={{
        fontSize: '0.75rem', color: '#2D8B87', textTransform: 'uppercase',
        letterSpacing: '0.12em', marginBottom: '16px', fontWeight: '600'
      }}>Observe</div>

      <p style={{ fontSize: '1rem', color: '#444', marginBottom: '24px', lineHeight: '1.6' }}>
        Take a moment. What do you notice right now?
      </p>

      {/* Post-scan — mirrors the pre-scan signals */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(30,107,104,0.04) 0%, rgba(184,211,207,0.12) 100%)',
        borderRadius: '16px', padding: '24px', marginBottom: '24px',
        borderLeft: '3px solid #2D8B87'
      }}>
        <p style={{
          margin: '0 0 20px', fontSize: '0.78rem', color: '#8FA3A0',
          letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '600'
        }}>Post-practice body scan</p>

        <ScanSlider
          label="Activation" sublabel="How much activation or tension do you notice now?"
          min={1} max={10} value={postActivation} onChange={setPostActivation}
          testId="post-activation-slider" leftLabel="1 — calm" rightLabel="10 — activated"
        />
        <ScanSlider
          label="Presence" sublabel="How connected are you to your body right now?"
          min={1} max={5} value={postPresence} onChange={setPostPresence}
          testId="post-presence-slider" leftLabel="Disconnected" rightLabel="Fully present"
        />
        <ScanSlider
          label="Openness" sublabel="How receptive does your system feel now?"
          min={1} max={5} value={postOpenness} onChange={setPostOpenness}
          testId="post-openness-slider" leftLabel="Contracted" rightLabel="Open"
        />
      </div>

      {/* Live delta — shown only if we have pre-scan data */}
      {preActivation && (
        <SessionDelta pre={preData} post={{ activation: postActivation, presence: postPresence, openness: postOpenness }} />
      )}

      {/* Optional note */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', color: '#1E6B68', fontWeight: '600', fontSize: '0.88rem', marginBottom: '8px' }}>
          Anything else? <span style={{ fontWeight: '400', color: '#999' }}>(optional)</span>
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="A word, a sensation, a thought..."
          data-testid="observe-note"
          rows={2}
          style={{
            width: '100%', padding: '12px', borderRadius: '12px',
            border: '2px solid #B8D3CF', fontSize: '1rem', resize: 'none'
          }}
        />
      </div>
      
      <button
        onClick={handleSubmit}
        data-testid="observe-complete-btn"
        style={{
          padding: '16px 40px', width: '100%',
          background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
          color: '#fff', border: 'none', borderRadius: '24px',
          fontSize: '1.1rem', fontWeight: '600', cursor: 'pointer'
        }}
      >Continue →</button>
      
      <button
        onClick={onSkip}
        data-testid="observe-skip-btn"
        style={{
          marginTop: '12px', padding: '12px', width: '100%',
          background: 'transparent', color: '#999',
          border: 'none', fontSize: '0.9rem', cursor: 'pointer'
        }}
      >Skip for today</button>
    </div>
  );
}

// Integrate Step View — with session delta summary
function IntegrateStep({ content, onComplete, sessionPre, sessionPost }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', borderRadius: '24px',
      padding: '32px', boxShadow: '0 4px 20px rgba(30,107,104,0.08)'
    }}>
      <div style={{
        fontSize: '0.75rem', color: '#2D8B87', textTransform: 'uppercase',
        letterSpacing: '0.12em', marginBottom: '16px', fontWeight: '600'
      }}>Integrate</div>

      <p style={{
        fontFamily: "'Georgia', serif", fontSize: '1.15rem',
        lineHeight: '1.8', color: '#333', margin: '0 0 28px'
      }}>{content}</p>

      {/* Session delta — shows shift across all 3 signals */}
      {sessionPre?.activation && sessionPost?.activation && (
        <div style={{ marginBottom: '24px' }}>
          <SessionDelta pre={sessionPre} post={sessionPost} />
        </div>
      )}

      <button
        onClick={onComplete}
        style={{
          padding: '16px 40px', width: '100%',
          background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
          color: '#fff', border: 'none', borderRadius: '24px',
          fontSize: '1rem', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.02em'
        }}
      >Complete Practice</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PREDICTIVE CARD — Daily emotional forecast
// Shows when there's an amber/red flag or a meaningful positive pattern.
// ─────────────────────────────────────────────────────────────────────────────
const RISK_STYLES = {
  green:  { bg: 'rgba(45,139,135,0.05)',  border: 'rgba(45,139,135,0.18)',  accent: '#2D8B87', dot: '#4AACA8',  label: 'Clear forecast'    },
  amber:  { bg: 'rgba(184,144,106,0.06)', border: 'rgba(184,144,106,0.25)', accent: '#B8906A', dot: '#D4A86A',  label: 'Amber flag today'  },
  red:    { bg: 'rgba(163,59,43,0.05)',   border: 'rgba(163,59,43,0.22)',   accent: '#A33B2B', dot: '#C84830',  label: 'High load predicted'},
};

function PredictiveCard({ userId }) {
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const key = `hypseya_forecast_dismissed_${new Date().toDateString()}`;
    if (localStorage.getItem(key)) { setLoading(false); setDismissed(true); return; }
    if (userId) fetchForecast();
  }, [userId]);

  const fetchForecast = async () => {
    try {
      const res = await axios.get(`${API}/intelligence/forecast/${userId}`);
      if (res.data.has_data) setForecast(res.data);
    } catch (e) {}
    setLoading(false);
  };

  const dismiss = () => {
    localStorage.setItem(`hypseya_forecast_dismissed_${new Date().toDateString()}`, '1');
    setDismissed(true);
  };

  // Only show if amber/red OR there's a meaningful positive pattern to highlight
  if (loading || dismissed || !forecast) return null;
  if (forecast.risk_level === 'green' && !forecast.patterns?.length) return null;

  const style = RISK_STYLES[forecast.risk_level] || RISK_STYLES.green;

  return (
    <div style={{
      maxWidth: '600px', margin: '0 auto 8px', padding: '0 20px',
    }}>
      <div style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        borderLeft: `3px solid ${style.accent}`,
        borderRadius: '16px',
        padding: '14px 16px',
        position: 'relative',
      }}>
        <button onClick={dismiss} style={{
          position: 'absolute', top: '10px', right: '12px',
          background: 'none', border: 'none', color: '#A0B4B0',
          fontSize: '16px', cursor: 'pointer', lineHeight: 1,
        }}>×</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
          <span style={{
            width: '7px', height: '7px', borderRadius: '50%',
            background: style.dot, display: 'inline-block', flexShrink: 0,
          }} />
          <span style={{ fontSize: '10px', fontWeight: 700, color: style.accent,
            letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {style.label}
          </span>
        </div>

        {forecast.suggestion && (
          <p style={{ margin: '0 0 8px', fontSize: '13px', lineHeight: 1.6, color: '#1E3A39', fontWeight: 500, paddingRight: '20px' }}>
            {forecast.suggestion}
          </p>
        )}

        {forecast.triggers?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {forecast.triggers.map((t, i) => (
              <p key={i} style={{ margin: 0, fontSize: '11px', color: '#6B8B87', lineHeight: 1.5 }}>
                · {t}
              </p>
            ))}
          </div>
        )}

        {forecast.risk_level === 'green' && forecast.patterns?.length > 0 && (
          <p style={{ margin: 0, fontSize: '11px', color: '#2D8B87', lineHeight: 1.5 }}>
            ✦ {forecast.patterns[0]}
          </p>
        )}
      </div>
    </div>
  );
}


// Completion View
// ── ERI Widget — compact Home version, links to Insights ─────────────────────
function ERIWidget({ userId }) {
  const navigate = useNavigate();
  const [eri, setEri]         = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!userId) return;
    Promise.all([
      axios.get(`${API}/training/eri/${userId}`),
      axios.get(`${API}/training/eri-history/${userId}?limit=5`),
    ]).then(([r1, r2]) => {
      setEri(r1.data);
      setHistory(r2.data.scores || []);
    }).catch(() => {});
  }, [userId]);

  if (!eri || eri.eri_score === null) return null;

  const bandColor = eri.eri_score >= 80 ? '#1E6B68'
    : eri.eri_score >= 60 ? '#2D8B87'
    : eri.eri_score >= 40 ? '#4AACA8'
    : '#B8906A';

  const bandLabel = { strong: 'Strong', growing: 'Growing', emerging: 'Emerging', early: 'Early' }[eri.interpretation_band] || 'Building';
  const scores = history.slice(0, 5).reverse();

  return (
    <div
      onClick={() => navigate('/insights')}
      style={{
        background: 'rgba(255,255,255,0.95)', borderRadius: '16px',
        padding: '14px 18px', marginBottom: '12px',
        border: '1px solid rgba(184,211,207,0.3)',
        boxShadow: '0 2px 12px rgba(30,107,104,0.06)',
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px',
        transition: 'box-shadow 0.2s ease',
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 20px rgba(30,107,104,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(30,107,104,0.06)'}
    >
      {/* Score ring */}
      <div style={{
        width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
        background: `conic-gradient(${bandColor} ${eri.eri_score}%, rgba(184,211,207,0.2) 0%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: bandColor }}>
            {Math.round(eri.eri_score)}
          </span>
        </div>
      </div>

      {/* Label + sparkline */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.6rem', color: '#8FA3A0', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: '700', marginBottom: '2px' }}>
          ERI
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '0.88rem', fontWeight: '700', color: bandColor }}>{bandLabel}</span>
          {scores.length >= 2 && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '16px' }}>
              {scores.map((s, i) => (
                <div key={i} style={{
                  width: 4, borderRadius: '2px',
                  height: `${Math.max(3, (s.eri_score / 100) * 16)}px`,
                  background: i === scores.length - 1 ? bandColor : 'rgba(184,211,207,0.5)',
                  transition: 'height 0.4s ease',
                }} />
              ))}
            </div>
          )}
        </div>
        <div style={{ fontSize: '0.7rem', color: '#AAB8B6', marginTop: '1px' }}>
          {eri.entries_analyzed} sessions · {eri.period_days}d — full analysis →
        </div>
      </div>

      <ArrowRight size={14} color="#B8D3CF" />
    </div>
  );
}

// ERI Progress Card Component
function ERIProgressCard({ userId, isPro = false }) {
  const [eriData, setEriData] = useState(null);
  const [eriHistory, setEriHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTrend, setShowTrend] = useState(false);

  useEffect(() => {
    if (userId) {
      loadERI();
    }
  }, [userId]);

  const loadERI = async () => {
    try {
      const [eriResponse, historyResponse] = await Promise.all([
        axios.get(`${API}/training/eri/${userId}`),
        axios.get(`${API}/training/eri-history/${userId}?limit=7`)
      ]);
      setEriData(eriResponse.data);
      setEriHistory(historyResponse.data.scores || []);
    } catch (error) {
      console.error('Failed to load ERI:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getBandColor = (band) => {
    switch(band) {
      case 'strong':   return '#1E6B68';
      case 'growing':  return '#2D8B87';
      case 'emerging': return '#4AACA8';
      case 'early':    return '#B8906A';
      default:         return '#B8D3CF';
    }
  };

  const getBandLabel = (band) => {
    switch(band) {
      case 'strong': return 'Strong Regulation';
      case 'growing': return 'Growing Stability';
      case 'emerging': return 'Emerging Regulation';
      case 'early': return 'Early Phase';
      default: return 'Building Data';
    }
  };

  if (isLoading) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.9)', borderRadius: '16px',
        padding: '20px', marginBottom: '20px', textAlign: 'center'
      }}>
        <p style={{ color: '#999', fontSize: '0.9rem' }}>Loading ERI...</p>
      </div>
    );
  }

  if (!eriData || eriData.eri_score === null) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.9)', borderRadius: '16px',
        padding: '20px', marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #B8D3CF 0%, #E8F5E9 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Chart size={22} color="#1E6B68" />
          </div>
          <div>
            <h4 style={{ margin: 0, color: '#1E6B68', fontSize: '1rem' }}>Emotional Regulation Index</h4>
            <p style={{ margin: 0, color: '#999', fontSize: '0.8rem' }}>Building your baseline</p>
          </div>
        </div>
        <p style={{ color: '#666', fontSize: '0.85rem', margin: 0 }}>
          Complete 3+ practices with activation check-ins to see your ERI score.
        </p>
      </div>
    );
  }

  const bandColor = getBandColor(eriData.interpretation_band);

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.95)', borderRadius: '16px',
      padding: '20px', marginBottom: '20px',
      boxShadow: '0 2px 12px rgba(30, 107, 104, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <h4 style={{ margin: '0 0 4px', color: '#1E6B68', fontSize: '0.9rem', fontWeight: '500' }}>
            Emotional Regulation Index
          </h4>
          <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>
            Last {eriData.period_days} days • {eriData.entries_analyzed} sessions
          </p>
        </div>
        <div style={{
          background: `${bandColor}20`,
          color: bandColor,
          padding: '4px 10px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: '500'
        }}>
          {getBandLabel(eriData.interpretation_band)}
        </div>
      </div>

      {/* Main Score */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '16px' }}>
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%',
          background: `conic-gradient(${bandColor} ${eriData.eri_score}%, #E8F5E9 0%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative'
        }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: '#fff', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center'
          }}>
            <span style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1E6B68' }}>
              {Math.round(eriData.eri_score)}
            </span>
          </div>
        </div>

        {/* Sub-scores */}
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '2px' }}>
              <span style={{ color: '#666' }}>Regulation</span>
              <span style={{ color: '#1E6B68' }}>{Math.round(eriData.regulation_capacity)}</span>
            </div>
            <div style={{ height: '4px', background: '#E8F5E9', borderRadius: '2px' }}>
              <div style={{ 
                height: '100%', width: `${eriData.regulation_capacity}%`, 
                background: '#1E6B68', borderRadius: '2px', transition: 'width 0.5s'
              }} />
            </div>
          </div>
          <div style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '2px' }}>
              <span style={{ color: '#666' }}>Stability</span>
              <span style={{ color: '#2D8B87' }}>{Math.round(eriData.stability_score)}</span>
            </div>
            <div style={{ height: '4px', background: '#E8F5E9', borderRadius: '2px' }}>
              <div style={{ 
                height: '100%', width: `${eriData.stability_score}%`, 
                background: '#2D8B87', borderRadius: '2px', transition: 'width 0.5s'
              }} />
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '2px' }}>
              <span style={{ color: '#666' }}>Consistency</span>
              <span style={{ color: '#2D8B87' }}>{Math.round(eriData.practice_consistency)}</span>
            </div>
            <div style={{ height: '4px', background: 'rgba(184,211,207,0.3)', borderRadius: '2px' }}>
              <div style={{
                height: '100%', width: `${eriData.practice_consistency}%`,
                background: 'linear-gradient(90deg, #1E6B68, #4AACA8)', borderRadius: '2px', transition: 'width 0.5s'
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Mini Trend (last 7 ERI scores) */}
      {eriHistory.length >= 2 && (
        <div style={{
          borderTop: '1px solid #E8F5E9', paddingTop: '12px',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <span style={{ fontSize: '0.7rem', color: '#999' }}>Trend:</span>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '24px' }}>
            {eriHistory.slice(0, 7).reverse().map((score, idx) => (
              <div
                key={idx}
                style={{
                  width: '8px',
                  height: `${Math.max(4, (score.eri_score / 100) * 24)}px`,
                  background: idx === eriHistory.length - 1 ? bandColor : '#B8D3CF',
                  borderRadius: '2px',
                  transition: 'height 0.3s'
                }}
                title={`ERI: ${Math.round(score.eri_score)}`}
              />
            ))}
          </div>
          {eriHistory.length >= 2 && (() => {
            const rising = eriHistory[0].eri_score > eriHistory[eriHistory.length-1].eri_score;
            const falling = eriHistory[0].eri_score < eriHistory[eriHistory.length-1].eri_score;
            const trendColor = rising ? '#2D8B87' : falling ? '#B8906A' : '#999';
            return (
              <span style={{ display:'inline-flex', alignItems:'center' }}>
                {rising && <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><line x1="10" y1="17" x2="10" y2="4" stroke={trendColor} strokeWidth="1.8" strokeLinecap="round"/><polyline points="5.5,9 10,4 14.5,9" stroke={trendColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>}
                {falling && <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><line x1="10" y1="3" x2="10" y2="16" stroke={trendColor} strokeWidth="1.8" strokeLinecap="round"/><polyline points="5.5,11 10,16 14.5,11" stroke={trendColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>}
                {!rising && !falling && <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><line x1="3" y1="10" x2="17" y2="10" stroke={trendColor} strokeWidth="1.8" strokeLinecap="round"/></svg>}
              </span>
            );
          })()}
        </div>
      )}

      {/* Metadata footer */}
      {eriData.avg_regulation_delta !== null && (
        <div style={{
          marginTop: '12px', padding: '8px 12px',
          background: 'rgba(30, 107, 104, 0.04)', borderRadius: '8px',
          fontSize: '0.75rem', color: '#666'
        }}>
          Avg regulation: {eriData.avg_regulation_delta > 0 ? '↓' : '↑'}{Math.abs(eriData.avg_regulation_delta)} points 
          ({eriData.avg_pre_activation} → {eriData.avg_post_activation})
        </div>
      )}

      {/* View Trend Button */}
      <button
        data-testid="eri-view-trend-btn"
        onClick={() => setShowTrend(!showTrend)}
        style={{
          width: '100%', marginTop: '12px', padding: '10px',
          background: showTrend ? 'rgba(30, 107, 104, 0.08)' : 'transparent',
          border: '1px solid rgba(30, 107, 104, 0.15)',
          borderRadius: '10px', cursor: 'pointer',
          color: '#1E6B68', fontSize: '0.8rem', fontWeight: '500',
          transition: 'all 0.2s ease',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
        </svg>
        {showTrend ? 'Hide Trend' : 'View ERI Trend'}
      </button>

      {/* Expanded Trend Chart */}
      {showTrend && (
        <div style={{ marginTop: '16px' }}>
          <ERITrendChart userId={userId} onClose={() => setShowTrend(false)} isPro={isPro} />
        </div>
      )}
    </div>
  );
}


function CompletionView({ metrics, focusArea, userId, isPro = false }) {
  const navigate = useNavigate();
  const focusLabels = {
    anxiety: 'Anxiety & Calm',
    sleep: 'Sleep & Rest',
    confidence: 'Confidence & Self-Worth',
    grief: 'Grief & Processing',
    stress: 'Stress Resilience',
    regulation: 'Emotional Regulation',
    recovery: 'Recovery & Healing',
    wellbeing: 'General Wellbeing'
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)',
      borderRadius: '24px', padding: '32px',
      textAlign: 'center'
    }}>
      <div style={{ marginBottom: '16px', display:'flex', justifyContent:'center' }}><Leaf size={48} color="#2D8B87" /></div>
      <h2 style={{
        fontFamily: "'Playfair Display', serif",
        color: '#1E6B68', fontSize: '1.5rem', margin: '0 0 8px'
      }}>Practice complete</h2>
      <p style={{ color: '#666', marginBottom: '32px' }}>
        Your nervous system is learning. See you tomorrow.
      </p>
      
      {/* Metrics Display */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px',
        background: 'rgba(255, 255, 255, 0.8)', borderRadius: '16px',
        padding: '20px', marginBottom: '24px'
      }}>
        <div>
          <div style={{ fontSize: '1.8rem', color: '#1E6B68', fontWeight: '600' }}>
            {metrics?.total_practices || 0}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Practices</div>
        </div>
        <div>
          <div style={{ fontSize: '1.8rem', color: '#1E6B68', fontWeight: '600' }}>
            {metrics?.weeks_consistent || 0}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>Weeks</div>
        </div>
        <div>
          <div style={{ fontSize: '1.8rem', color: '#1E6B68', fontWeight: '600' }}>
            {metrics?.current_month_count || 0}
          </div>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>This month</div>
        </div>
      </div>
      
      {/* ERI Progress Card */}
      {userId && (
        <div style={{ marginTop: '24px', textAlign: 'left' }}>
          <ERIProgressCard userId={userId} isPro={isPro} />
        </div>
      )}

      {/* Doorways — the nervous system is at its most open right now.
          Guide it into reflection (deepens the record) or its progress. */}
      <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <button
          onClick={() => navigate('/journal')}
          style={{
            width: '100%', padding: '15px 20px',
            background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
            color: '#fff', border: 'none', borderRadius: '16px',
            fontSize: '0.95rem', fontWeight: '600', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 4px 16px rgba(30,107,104,0.22)',
          }}
        >
          <span style={{ textAlign: 'left' }}>
            Reflect on what moved
            <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: '400', opacity: 0.85, marginTop: '2px' }}>
              Your Journal has a question waiting.
            </span>
          </span>
          <span style={{ fontSize: '1.1rem' }}>→</span>
        </button>
        <button
          onClick={() => navigate('/insights')}
          style={{
            width: '100%', padding: '12px 20px',
            background: 'rgba(30,107,104,0.06)', color: '#1E6B68',
            border: '1px solid rgba(30,107,104,0.2)', borderRadius: '16px',
            fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
        >
          See your progress →
        </button>
      </div>

      <p style={{ margin: '18px 0 0', fontSize: '0.78rem', color: '#9AADAB' }}>
        Focus: {focusLabels[focusArea] || 'Wellbeing'}
      </p>
    </div>
  );
}

// ── Integration Window helpers ────────────────────────────────────────────────
const IW_KEY = 'hyp_integration_window';
const IW_DAYS = 30;

function getIntegrationWindow() {
  try { return JSON.parse(localStorage.getItem(IW_KEY)); } catch { return null; }
}
function startIntegrationWindow(label = '') {
  const w = { startedAt: new Date().toISOString(), label };
  localStorage.setItem(IW_KEY, JSON.stringify(w));
  return w;
}
function getWindowDay(startedAt) {
  return Math.min(Math.floor((Date.now() - new Date(startedAt)) / 86400000) + 1, IW_DAYS);
}

// Main Home Component
function Home() {
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();
  const subscription = useSubscription(user?.sub);
  const isPro = subscription?.isPro ?? false;

  // Redirect non-patient roles away from the Daily Loop immediately
  const storedRole = localStorage.getItem('hypseya_user_role') || 'user';
  if (storedRole === 'therapist') {
    navigate('/therapist-dashboard', { replace: true });
    return null;
  }
  if (storedRole === 'doctor') {
    navigate('/doctor-dashboard', { replace: true });
    return null;
  }
  if (storedRole === 'institution') {
    navigate('/practitioner', { replace: true });
    return null;
  }

  // Redirect matrescence users to their hub
  const storedMode = localStorage.getItem('hypseya_user_mode');
  const cachedUser = JSON.parse(localStorage.getItem('user') || '{}');
  if (storedMode === 'matrescence' || cachedUser?.mode === 'matrescence') {
    navigate('/matrescence', { replace: true });
    return null;
  }

  // Helper: returns axios config with Auth0 Bearer token
  const authConfig = async (extra = {}) => {
    try {
      const token = await getAccessTokenSilently();
      return { ...extra, headers: { Authorization: `Bearer ${token}` } };
    } catch {
      return extra;
    }
  };
  const [userData, setUserData] = useState(null);
  const [trainingProfile, setTrainingProfile] = useState(null);
  const [dailyLoop, setDailyLoop] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showArrival, setShowArrival] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [integrationWindow, setIntegrationWindow] = useState(getIntegrationWindow);
  const [showIWPrompt, setShowIWPrompt] = useState(false);
  const [showAnchor, setShowAnchor] = useState(false);
  const [showSomatic, setShowSomatic] = useState(false);
  // Crisis & support
  const [showCrisis, setShowCrisis] = useState(false);
  const [crisisProtocol, setCrisisProtocol] = useState(null);
  // Therapist prescriptions
  const [prescriptions, setPrescriptions] = useState([]);
  // Adaptive practice engine — today's recommendation
  const [practiceRec, setPracticeRec] = useState(null);

  // Check if user has seen arrival today
  useEffect(() => {
    const today = new Date().toDateString();
    const lastArrival = sessionStorage.getItem('lastArrivalSeen');
    if (lastArrival === today) {
      setShowArrival(false);
    }
  }, []);

  useEffect(() => {
    if (user?.sub) {
      loadData();
    }
  }, [user]);

  const handleArrivalComplete = () => {
    const today = new Date().toDateString();
    sessionStorage.setItem('lastArrivalSeen', today);
    setShowArrival(false);
  };

  // Fetch today's practice recommendation when the user reaches the Practice
  // step — pre_activation from Orient lets the engine handle acute states.
  useEffect(() => {
    if (currentStep !== 2 || !user?.sub || practiceRec) return;
    (async () => {
      try {
        const params = {};
        if (dailyLoop?.pre_activation) params.pre_activation = dailyLoop.pre_activation;
        const cfg = await authConfig({ params });
        const r = await axios.get(`${API}/training/practice-recommendation/${user.sub}`, cfg);
        setPracticeRec(r.data);
      } catch {
        setPracticeRec({ recommended: null, library: [], effectiveness: [] });
      }
    })();
  }, [currentStep, user, dailyLoop?.pre_activation]); // eslint-disable-line

  const loadData = async () => {
    try {
      setIsLoading(true);
      const userId = user.sub;
      const cfg = await authConfig();

      // Load user data (non-blocking)
      try {
        const userResponse = await axios.get(`${API}/users/${user.email}`, cfg);
        setUserData(userResponse.data);
      } catch (e) {
        // New user, no profile yet
      }

      // Load training profile (graceful fallback)
      let profile = null;
      try {
        const profileResponse = await axios.get(`${API}/training/profile/${userId}`, cfg);
        profile = profileResponse.data;
        setTrainingProfile(profile);
      } catch (e) {
        // No profile yet
      }

      if (!profile) {
        setShowOnboarding(true);
      } else {
        // Load daily loop and metrics (graceful fallback)
        try {
          const [loopResponse, metricsResponse] = await Promise.all([
            axios.get(`${API}/training/daily/${userId}`, cfg),
            axios.get(`${API}/training/metrics/${userId}`, cfg)
          ]);
          setDailyLoop(loopResponse.data);
          setMetrics(metricsResponse.data);

          const loop = loopResponse.data;
          if (loop.is_complete || loop.integrate_completed) {
            setCurrentStep(5);
          } else if (loop.observe_completed || loop.is_short_version) {
            setCurrentStep(4);
          } else if (loop.practice_completed) {
            setCurrentStep(3);
          } else if (loop.orient_completed) {
            setCurrentStep(2);
          } else {
            setCurrentStep(1);
          }
        } catch (e) {
          setCurrentStep(1);
        }
      }
      // Load prescriptions + crisis protocol in background (non-blocking)
      axios.get(`${API}/users/${userId}/prescriptions`).then(r => {
        setPrescriptions(r.data?.prescriptions || []);
      }).catch(() => {});
      axios.get(`${API}/users/${userId}/crisis-protocol`).then(r => {
        setCrisisProtocol(r.data);
      }).catch(() => {});

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const dismissPrescription = async (id) => {
    try {
      await axios.patch(`${API}/prescriptions/${id}/dismiss`);
      setPrescriptions(prev => prev.filter(p => p.id !== id));
    } catch {}
  };

  const handleOnboardingComplete = async (focusArea, horizonDays) => {
    try {
      const userId = user.sub;
      const cfg = await authConfig({ params: { user_id: userId, focus_area: focusArea, horizon_days: horizonDays } });
      await axios.post(`${API}/training/profile`, null, cfg);
      
      setShowOnboarding(false);
      await loadData();
      toast.success('Welcome to your practice journey');
    } catch (error) {
      toast.error('Failed to save preferences');
    }
  };

  const completeOrient = async ({ pre_activation, pre_presence, pre_openness } = {}) => {
    try {
      const params = {
        pre_activation,
        pre_presence,
        pre_openness,
        timezone_offset: new Date().getTimezoneOffset(),
        local_hour: new Date().getHours()
      };
      const cfg = await authConfig({ params });
      await axios.post(`${API}/training/daily/${user.sub}/orient`, null, cfg);
      setCurrentStep(2);
      setDailyLoop(prev => ({
        ...prev, orient_completed: true,
        pre_activation, pre_presence, pre_openness
      }));
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const completePractice = async (practiceId, duration) => {
    try {
      const cfg = await authConfig({ params: {
        practice_id: practiceId || undefined,
        duration_minutes: duration
      } });
      await axios.post(`${API}/training/daily/${user.sub}/practice`, null, cfg);
      setCurrentStep(3);
      setDailyLoop(prev => ({ ...prev, practice_completed: true }));
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const completeObserve = async (responses) => {
    try {
      const cfg = await authConfig({ params: responses });
      await axios.post(`${API}/training/daily/${user.sub}/observe`, null, cfg);
      setCurrentStep(4);
      setDailyLoop(prev => ({
        ...prev, observe_completed: true,
        post_activation: responses.post_activation,
        post_presence:   responses.post_presence,
        post_openness:   responses.post_openness
      }));
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const skipObserve = async () => {
    try {
      const cfg = await authConfig();
      await axios.post(`${API}/training/daily/${user.sub}/skip-observe`, null, cfg);
      setCurrentStep(4);
      setDailyLoop(prev => ({ ...prev, is_short_version: true }));
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const completeIntegrate = async () => {
    try {
      const cfg = await authConfig();
      await axios.post(`${API}/training/daily/${user.sub}/integrate`, null, cfg);
      setCurrentStep(5);
      setDailyLoop(prev => ({ ...prev, integrate_completed: true, is_complete: true }));

      // Refresh metrics
      const metricsResponse = await axios.get(`${API}/training/metrics/${user.sub}`, cfg);
      setMetrics(metricsResponse.data);
      
      toast.success('Practice complete.');
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #fafbfc 0%, #f0f7f6 40%, #e8f3f1 70%, #f5f8f7 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="hyp-orb-wrapper" style={{ width: '80px', height: '80px', margin: '0 auto' }}>
            <span className="hyp-ripple-1" />
            <span className="hyp-ripple-2" />
            <img src={orbUrl} alt="HYPSEYA" style={{ width: '100%' }} />
          </div>
          <img src={wordmarkUrl} alt="HYPSEYA" style={{ height: '16px', marginTop: '16px', opacity: 0.6 }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F7F3EC 0%, #EFE7DD 50%, #B8D3CF 100%)',
      padding: '0 0 100px'
    }}>
      {/* Crisis Screen */}
      {showCrisis && (
        <CrisisScreen
          protocol={crisisProtocol}
          onContinue={() => setShowCrisis(false)}
          onDismiss={() => setShowCrisis(false)}
        />
      )}

      {/* Arrival Screen - Shows first, sets the tone */}
      {showArrival && (
        <ArrivalScreen onContinue={handleArrivalComplete} />
      )}

      {/* Welcome / Getting Started guide — sessions 1–3 */}
      {!showArrival && <WelcomeSheet />}

      {/* Onboarding Modal - Only after arrival */}
      {!showArrival && showOnboarding && (
        <FocusHorizonModal
          onComplete={handleOnboardingComplete}
          onClose={() => setShowOnboarding(false)}
        />
      )}

      {/* Header */}
      <div style={{ padding: '28px 24px 20px', textAlign: 'center' }}>
        {/* Orb + wordmark */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div className="hyp-orb-wrapper" style={{ width: '112px', height: '112px' }}>
            <span className="hyp-ripple-1" />
            <span className="hyp-ripple-2" />
            <img src={orbUrl} alt="" style={{ width: '100%' }} />
          </div>
          <img src={wordmarkUrl} alt="HYPSEYA" style={{ height: '18px', width: 'auto', opacity: 0.75 }} />
        </div>

        {/* Greeting */}
        <p style={{
          margin: '0 0 4px',
          fontSize: '0.72rem', fontWeight: '600',
          letterSpacing: '0.08em', color: '#A8B5B3',
          textTransform: 'uppercase'
        }}>
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: currentStep === 5 ? '1.5rem' : '1.7rem',
          color: '#1E6B68', fontWeight: '400', margin: '0 0 4px'
        }}>
          {currentStep === 5
            ? 'Practice Complete'
            : `${(() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; })()}${user?.name ? `, ${user.name.split(' ')[0]}` : ''}.`}
        </h2>
        {currentStep < 5 && (
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#9AADAB' }}>
            {integrationWindow
              ? `Day ${getWindowDay(integrationWindow.startedAt)} of your integration window.`
              : (dailyLoop?.coaching_checkin?.message || 'Your integration practice is ready.')}
          </p>
        )}

        {/* Streak badge — only when >= 2 consecutive days */}
        {trainingProfile?.current_streak >= 2 && currentStep < 5 && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '7px',
            marginTop: '12px',
            padding: '5px 14px 5px 10px',
            background: 'linear-gradient(135deg, rgba(30,107,104,0.1) 0%, rgba(45,139,135,0.07) 100%)',
            borderRadius: '20px',
            border: '1px solid rgba(30,107,104,0.18)',
          }}>
            {/* Animated flame-like dot */}
            <span style={{
              width: '7px', height: '7px', borderRadius: '50%',
              background: trainingProfile.current_streak >= 7
                ? 'linear-gradient(135deg, #F57C00, #FFA726)'
                : 'linear-gradient(135deg, #1E6B68, #2D8B87)',
              boxShadow: trainingProfile.current_streak >= 7
                ? '0 0 6px rgba(245,124,0,0.5)'
                : '0 0 6px rgba(30,107,104,0.4)',
              animation: 'streakPulse 2s ease-in-out infinite',
              flexShrink: 0,
            }} />
            <span style={{ fontSize: '0.76rem', fontWeight: '600', color: '#1E6B68', letterSpacing: '0.02em' }}>
              {trainingProfile.current_streak} days in a row
            </span>
          </div>
        )}
        <style>{`@keyframes streakPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.15)} }`}</style>

        {/* SOS — "I need support" button. Discreet but findable: someone in
            distress should not have to hunt for it. */}
        {!showArrival && (
          <button
            onClick={() => setShowCrisis(true)}
            style={{
              marginTop: '14px',
              background: 'rgba(184,144,106,0.09)',
              border: '1px solid rgba(184,144,106,0.28)',
              borderRadius: '16px',
              color: '#8A6D52', fontSize: '0.75rem', fontWeight: '600',
              cursor: 'pointer', padding: '7px 16px',
              letterSpacing: '0.04em',
            }}
          >
            I need support right now
          </button>
        )}
      </div>

      {/* ERI — surfaced right under the greeting: the progress number should
          be visible every day, not only after completing the loop. */}
      {trainingProfile && user?.sub && currentStep !== 5 && !showArrival && (
        <div style={{ maxWidth: '600px', margin: '0 auto 12px', padding: '0 20px' }}>
          <ERIWidget userId={user.sub} />
        </div>
      )}

      {/* Therapist Prescription card */}
      {prescriptions.length > 0 && !showArrival && (
        <div style={{ padding: '0 20px', marginBottom: '4px' }}>
          {prescriptions.map(p => (
            <div key={p.id} style={{
              background: 'rgba(255,255,255,0.9)',
              borderRadius: '16px',
              border: '1.5px solid rgba(30,107,104,0.25)',
              padding: '14px 16px',
              marginBottom: '8px',
              position: 'relative',
            }}>
              <p style={{ margin: '0 0 2px', fontSize: '0.65rem', fontWeight: '700', color: '#1E6B68', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                From {p.therapist_name}
              </p>
              <p style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '0.9rem', color: '#1E3A3A', paddingRight: '28px' }}>
                {p.title}
              </p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#6A8F8C', lineHeight: 1.5 }}>
                {p.message}
              </p>
              <button
                onClick={() => dismissPrescription(p.id)}
                style={{
                  position: 'absolute', top: '12px', right: '12px',
                  background: 'none', border: 'none', color: '#B8D3CF',
                  fontSize: '1rem', cursor: 'pointer', padding: '2px',
                }}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Progress Dots */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        padding: '0 20px 32px',
        position: 'relative',
        maxWidth: '340px',
        margin: '0 auto'
      }}>
        {/* Connecting Line - center of first circle to center of last */}
        <div style={{
          position: 'absolute', top: '24px',
          left: '44px', right: '44px',
          height: '1.5px', background: 'rgba(184, 211, 207, 0.25)', zIndex: 0
        }} />
        
        <LoopStep 
          number={1} label="Orient" 
          isActive={currentStep === 1}
          isComplete={dailyLoop?.orient_completed}
        />
        <LoopStep 
          number={2} label="Practice" 
          isActive={currentStep === 2}
          isComplete={dailyLoop?.practice_completed}
        />
        <LoopStep 
          number={3} label="Observe" 
          isActive={currentStep === 3}
          isComplete={dailyLoop?.observe_completed}
        />
        <LoopStep 
          number={4} label="Integrate" 
          isActive={currentStep === 4}
          isComplete={dailyLoop?.integrate_completed}
        />
      </div>

      {/* ── Integration Window Banner ─────────────────────────────── */}
      {currentStep < 5 && (
        <div style={{ maxWidth: '600px', margin: '0 auto 16px', padding: '0 20px' }}>
          {integrationWindow ? (
            // Active window — banner
            <div style={{
              background: 'linear-gradient(135deg, rgba(30,107,104,0.08) 0%, rgba(45,139,135,0.06) 100%)',
              border: '1px solid rgba(30,107,104,0.18)',
              borderRadius: '16px', padding: '16px 20px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '1.3rem' }}>◎</span>
                <div>
                  <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '600', color: '#1E6B68', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    Integration Window
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#7A9E9B' }}>
                    Day {getWindowDay(integrationWindow.startedAt)} of {IW_DAYS} · Keep showing up.
                  </p>
                </div>
              </div>
              {/* Progress dots */}
              <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', maxWidth: '80px', justifyContent: 'flex-end' }}>
                {Array.from({ length: IW_DAYS }).map((_, i) => (
                  <div key={i} style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    background: i < getWindowDay(integrationWindow.startedAt) ? '#1E6B68' : 'rgba(184,211,207,0.4)'
                  }} />
                ))}
              </div>
            </div>
          ) : (
            // No window — subtle trigger
            !showIWPrompt ? (
              <button
                onClick={() => setShowIWPrompt(true)}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  color: '#9AADAB', fontSize: '0.78rem', letterSpacing: '0.04em',
                  cursor: 'pointer', padding: '6px 0', textAlign: 'center',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                + Something significant happened? Mark an experience →
              </button>
            ) : (
              // Confirm prompt
              <div style={{
                background: 'rgba(255,255,255,0.88)', borderRadius: '16px',
                padding: '20px', border: '1px solid rgba(184,211,207,0.4)',
                boxShadow: '0 2px 12px rgba(30,107,104,0.06)'
              }}>
                <p style={{ margin: '0 0 6px', fontFamily: "'Playfair Display', serif", fontSize: '1rem', color: '#1E6B68', fontWeight: '400' }}>
                  Start an integration window
                </p>
                <p style={{ margin: '0 0 16px', fontSize: '0.82rem', color: '#9AADAB', lineHeight: '1.6' }}>
                  A 30-day practice window to anchor, track, and integrate what opened.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      const w = startIntegrationWindow();
                      setIntegrationWindow(w);
                      setShowIWPrompt(false);
                    }}
                    style={{
                      flex: 1, padding: '12px', borderRadius: '12px',
                      background: '#1E6B68', border: 'none',
                      color: '#fff', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer'
                    }}
                  >
                    Begin window
                  </button>
                  <button
                    onClick={() => setShowIWPrompt(false)}
                    style={{
                      padding: '12px 16px', borderRadius: '12px',
                      background: 'none', border: '1px solid rgba(184,211,207,0.5)',
                      color: '#9AADAB', fontSize: '0.85rem', cursor: 'pointer'
                    }}
                  >
                    Not now
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Predictive Forecast — amber/red flag or positive pattern */}
      {trainingProfile && user?.sub && currentStep === 1 && (
        <PredictiveCard userId={user.sub} />
      )}

      {/* Change focus area — subtle link */}
      {trainingProfile && currentStep !== 5 && (
        <div style={{ maxWidth: '600px', margin: '0 auto 4px', padding: '0 20px', textAlign: 'right' }}>
          <button
            onClick={() => setShowOnboarding(true)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '0.72rem', color: '#AAB8B6', fontWeight: '500',
              letterSpacing: '0.04em', padding: '4px 0',
              textDecoration: 'underline', textUnderlineOffset: '2px',
            }}
          >
            Change focus area
          </button>
        </div>
      )}

      {/* Main Content */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 20px' }}>
        {currentStep === 1 && dailyLoop && (
          <OrientStep
            content={dailyLoop.orient_content}
            programContext={dailyLoop.program_context}
            onComplete={completeOrient}
          />
        )}
        {currentStep === 1 && !dailyLoop && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ color: '#999', fontSize: '0.95rem', marginBottom: '20px' }}>
              Could not load today's session.
            </p>
            <button
              onClick={loadData}
              style={{ padding: '12px 28px', background: '#1E6B68', color: '#fff', border: 'none', borderRadius: '24px', fontSize: '0.9rem', cursor: 'pointer' }}
            >
              Try again
            </button>
          </div>
        )}
        
        {currentStep === 2 && (
          <PracticeStep
            recommendation={practiceRec}
            onComplete={completePractice}
          />
        )}
        
        {currentStep === 3 && (
          <ObserveStep
            onComplete={completeObserve}
            onSkip={skipObserve}
            preActivation={dailyLoop?.pre_activation}
            prePresence={dailyLoop?.pre_presence}
            preOpenness={dailyLoop?.pre_openness}
          />
        )}

        {currentStep === 4 && dailyLoop && (
          <IntegrateStep
            content={dailyLoop.integrate_content}
            onComplete={completeIntegrate}
            sessionPre={{
              activation: dailyLoop?.pre_activation,
              presence:   dailyLoop?.pre_presence,
              openness:   dailyLoop?.pre_openness
            }}
            sessionPost={{
              activation: dailyLoop?.post_activation,
              presence:   dailyLoop?.post_presence,
              openness:   dailyLoop?.post_openness
            }}
          />
        )}
        
        {currentStep === 5 && (
          <CompletionView
            metrics={metrics}
            focusArea={trainingProfile?.focus_area || dailyLoop?.focus_area}
            userId={user?.sub}
            isPro={isPro}
          />
        )}
      </div>

      {/* ── Quick-access floating buttons ─────────────────────────────────── */}
      {currentStep !== 1 && currentStep !== 2 && (
        <div style={{
          position: 'fixed', bottom: 90, right: 16, zIndex: 500,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {/* Âncora de Regulação */}
          <button
            onClick={() => setShowAnchor(true)}
            title="Regulation Anchor"
            style={{
              width: 50, height: 50, borderRadius: '50%',
              background: 'linear-gradient(135deg, #0F2421 0%, #1E6B68 100%)',
              border: '1.5px solid rgba(30,107,104,0.5)',
              boxShadow: '0 4px 20px rgba(30,107,104,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <circle cx="11" cy="11" r="4" fill="rgba(255,255,255,0.9)" />
              <circle cx="11" cy="11" r="7.5" stroke="rgba(255,255,255,0.4)" strokeWidth="1.2" />
              <circle cx="11" cy="11" r="10" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
            </svg>
          </button>

          {/* Check-in Somático */}
          <button
            onClick={() => setShowSomatic(true)}
            title="Somatic Check-in"
            style={{
              width: 50, height: 50, borderRadius: '50%',
              background: 'rgba(255,255,255,0.92)',
              border: '1.5px solid rgba(184,211,207,0.5)',
              boxShadow: '0 4px 16px rgba(30,107,104,0.14)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2a2 2 0 00-2 2v1H7a2 2 0 00-2 2v8a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-1V4a2 2 0 00-2-2z"
                stroke="#1E6B68" strokeWidth="1.4" fill="none"/>
              <path d="M8 11l1.5 1.5L13 9" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Relational card — shown when step complete or as discovery ── */}
      {currentStep === 5 && trainingProfile && (
        <div style={{ padding: '0 20px', marginTop: '8px' }}>
          <button
            onClick={() => navigate('/relational')}
            style={{
              width: '100%', background: 'none', border: 'none', cursor: 'pointer',
              padding: 0, textAlign: 'left',
            }}
          >
            <div style={{
              background: 'rgba(255,255,255,0.7)',
              borderRadius: '16px',
              padding: '14px 16px',
              border: '1px solid rgba(184,211,207,0.3)',
              display: 'flex', alignItems: 'center', gap: '14px',
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(74,172,168,0.15), rgba(30,107,104,0.1))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M11 14v-1.3a2.7 2.7 0 0 0-2.7-2.7H3.7A2.7 2.7 0 0 0 1 12.7V14" stroke="#1E6B68" strokeWidth="1.3" strokeLinecap="round"/>
                  <circle cx="6" cy="5" r="2.7" stroke="#1E6B68" strokeWidth="1.3"/>
                  <path d="M15 14v-1.3a2.7 2.7 0 0 0-2-2.61M10.3 2.09a2.7 2.7 0 0 1 0 5.23" stroke="#1E6B68" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 1px', fontWeight: '600', fontSize: '0.85rem', color: '#1E3A3A' }}>
                  Relational Regulation
                </p>
                <p style={{ margin: 0, fontSize: '0.73rem', color: '#9AADAB' }}>
                  Share your state · support each other
                </p>
              </div>
              <span style={{ color: '#B8D3CF', fontSize: '0.8rem' }}>→</span>
            </div>
          </button>
        </div>
      )}

      {/* ── Overlays ──────────────────────────────────────────────────────── */}
      {showAnchor && <AnchorOverlay onClose={() => setShowAnchor(false)} />}
      {showSomatic && (
        <SomaticCheckin
          userId={userData?.user_id || user?.sub}
          onClose={() => setShowSomatic(false)}
          context="standalone"
        />
      )}
    </div>
  );
}

export default Home;
